import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Switch,
  FormControlLabel,
  InputAdornment,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  FlashOn as RapidIcon,
  People as PeopleIcon,
  SkipNext as NextIcon,
  StopCircle as EndIcon,
  Search as SearchIcon,
  MusicNote as MusicIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';

// Styled components following SongQ design system
const ManagementContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#121212',
  minHeight: '100vh',
  padding: '24px',
}));

const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#1A1A1A',
  border: '1px solid #333',
  borderRadius: '12px',
  marginBottom: '24px',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#FF9800',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(255, 152, 0, 0.1)',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#FF9800',
  color: '#fff',
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '8px 16px',
  '&:hover': {
    backgroundColor: '#F57C00',
  },
  '&.MuiButton-outlined': {
    borderColor: '#FF9800',
    color: '#FF9800',
    '&:hover': {
      backgroundColor: 'rgba(255, 152, 0, 0.1)',
    },
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  backgroundColor: status === 'active' ? 'rgba(255, 152, 0, 0.2)' : 'rgba(158, 158, 158, 0.2)',
  color: status === 'active' ? '#FF9800' : '#9E9E9E',
  border: `1px solid ${status === 'active' ? '#FF9800' : '#9E9E9E'}`,
  fontWeight: 600,
}));

const RapidQuizManagement = () => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [participantsDialogOpen, setParticipantsDialogOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  // Song selection state
  const [availableSongs, setAvailableSongs] = useState([]);
  const [selectedSongs, setSelectedSongs] = useState([]);
  const [songSearchTerm, setSongSearchTerm] = useState('');
  const [loadingSongs, setLoadingSongs] = useState(false);
  const [difficulties, setDifficulties] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    quiz_name: '',
    quiz_config: {
      difficulty_id: null, // Will be set to first available difficulty
      questions_per_song: 3
    },
    quiz_data: {
      questions: [],
      songs: []
    },
    rapid_config: {
      questions_per_song: 2,
      total_questions: 20,
      scoring_type: 'speed_bonus',
      allow_skip: false,
      show_correct_answer: true,
      leaderboard_update_frequency: 'real_time',
      selected_songs: []
    },
    scheduled_start_time: '',
    scheduled_end_time: ''
  });

  // Fetch songs with questions (same logic as QuizQuestionManagement)
  const fetchSongsWithQuestions = useCallback(async (difficulty = null) => {
    try {
      setLoadingSongs(true);
      
      // First get all questions to aggregate by song
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/admin/questions`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch questions');
      }
      
      const questions = await response.json();
      
      // Filter by difficulty if specified
      let filteredQuestions = questions;
      if (difficulty && difficulty !== 'all') {
        filteredQuestions = questions.filter(q => q.difficulty_id === parseInt(difficulty));
      }
      
      // Aggregate questions by song (same logic as QuizQuestionManagement)
      const songMap = new Map();
      
      filteredQuestions.forEach(question => {
        const songKey = `${question.songs_master_id}-${question.song_name}`;
        
        if (!songMap.has(songKey)) {
          songMap.set(songKey, {
            songs_master_id: question.songs_master_id,
            name: question.song_name,
            artist: question.artist,
            question_count: 0
          });
        }
        
        const songData = songMap.get(songKey);
        songData.question_count++;
      });
      
      const songs = Array.from(songMap.values()).sort((a, b) => b.question_count - a.question_count);
      setAvailableSongs(songs);
    } catch (err) {
      console.error('Error fetching songs with questions:', err);
      enqueueSnackbar('Failed to fetch songs with questions', { variant: 'error' });
      setAvailableSongs([]);
    } finally {
      setLoadingSongs(false);
    }
  }, [enqueueSnackbar]);

  // Fetch difficulties
  const fetchDifficulties = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/quiz/config`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch difficulties');
      }
      
      const config = await response.json();
      setDifficulties(config.difficulties || []);
      
      // Set default difficulty to first available if not set
      if (config.difficulties && config.difficulties.length > 0 && !formData.quiz_config.difficulty_id) {
        setFormData(prev => ({
          ...prev,
          quiz_config: {
            ...prev.quiz_config,
            difficulty_id: config.difficulties[0].difficulty_id
          }
        }));
      }
    } catch (err) {
      console.error('Error fetching difficulties:', err);
      enqueueSnackbar('Failed to fetch difficulties', { variant: 'error' });
    }
  }, [enqueueSnackbar, formData.quiz_config.difficulty_id]);

  // Handle song selection
  const handleSongSelection = (song, isSelected) => {
    if (isSelected) {
      setSelectedSongs(prev => [...prev, song]);
    } else {
      setSelectedSongs(prev => prev.filter(s => s.songs_master_id !== song.songs_master_id));
    }
  };

  // Handle song search
  const handleSongSearch = (term) => {
    setSongSearchTerm(term);
  };

  // Get filtered songs based on search term
  const getFilteredSongs = () => {
    if (!songSearchTerm.trim()) {
      return availableSongs;
    }
    
    return availableSongs.filter(song => 
      song.name.toLowerCase().includes(songSearchTerm.toLowerCase()) ||
      song.artist.toLowerCase().includes(songSearchTerm.toLowerCase())
    );
  };

  // Fetch rapid quiz sessions
  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/rapid/sessions`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch rapid quiz sessions');
      }
      
      const data = await response.json();
      setSessions(data.sessions || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching rapid quiz sessions:', err);
      setError(err.message);
      enqueueSnackbar('Failed to fetch rapid quiz sessions', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  // Create rapid quiz session
  const createSession = async () => {
    try {
      // Validate form data
      if (!formData.quiz_name.trim()) {
        enqueueSnackbar('Quiz name is required', { variant: 'error' });
        return;
      }

      if (selectedSongs.length === 0) {
        enqueueSnackbar('Please select at least one song', { variant: 'error' });
        return;
      }

      const sessionData = {
        ...formData,
        quiz_type: 'rapid',
        quiz_config: {
          ...formData.quiz_config,
          difficulty_id: formData.quiz_config.difficulty_id,
          questions_per_song: formData.rapid_config.questions_per_song,
          number_of_songs: selectedSongs.length
        },
        quiz_data: {
          questions: [], // Will be populated when quiz starts
          songs: selectedSongs.map(song => ({
            songs_master_id: song.songs_master_id,
            name: song.name,
            artist: song.artist,
            question_count: song.question_count
          }))
        },
        rapid_config: {
          ...formData.rapid_config,
          selected_songs: selectedSongs.map(song => ({
            songs_master_id: song.songs_master_id,
            name: song.name,
            artist: song.artist
          }))
        }
      };

      // console.log('Creating rapid quiz session with data:', sessionData);

      const url = editingSession 
        ? `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions/${editingSession.session_id}`
        : `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions`;
      
      const method = editingSession ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify(sessionData)
      });

      if (!response.ok) {
        throw new Error('Failed to create rapid quiz session');
      }

      if (editingSession) {
        enqueueSnackbar('Rapid quiz session updated successfully!', { variant: 'success' });
        setEditDialogOpen(false);
      } else {
        enqueueSnackbar('Rapid quiz session created successfully!', { variant: 'success' });
        setCreateDialogOpen(false);
      }
      resetForm();
      fetchSessions();
    } catch (err) {
      enqueueSnackbar('Failed to create rapid quiz session', { variant: 'error' });
    }
  };

  // Activate/Deactivate session
  const toggleSessionStatus = async (sessionId, currentStatus) => {
    try {
      const action = currentStatus === 'active' ? 'deactivate' : 'activate';
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions/${sessionId}/${action}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to toggle session status');
      }

      const actionText = currentStatus === 'active' ? 'deactivated' : 'activated';
      enqueueSnackbar(`Session ${actionText} successfully!`, { variant: 'success' });
      fetchSessions();
    } catch (err) {
      enqueueSnackbar('Failed to toggle session status', { variant: 'error' });
    }
  };

  // Start next question
  const startNextQuestion = async (sessionId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/rapid/sessions/${sessionId}/next-question`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to start next question');
      }

      enqueueSnackbar('Next question started!', { variant: 'success' });
      fetchSessions();
    } catch (err) {
      enqueueSnackbar('Failed to start next question', { variant: 'error' });
    }
  };

  // End rapid quiz session
  const endRapidQuizSession = async (sessionId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/live-quiz/rapid/sessions/${sessionId}/end`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to end rapid quiz session');
      }

      enqueueSnackbar('Rapid quiz session ended!', { variant: 'success' });
      fetchSessions();
    } catch (err) {
      enqueueSnackbar('Failed to end rapid quiz session', { variant: 'error' });
    }
  };

  // Delete session
  const deleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this rapid quiz session?')) {
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete session');
      }

      enqueueSnackbar('Session deleted successfully!', { variant: 'success' });
      fetchSessions();
    } catch (err) {
      enqueueSnackbar('Failed to delete session', { variant: 'error' });
    }
  };

  // View quiz details
  const viewQuizDetails = (session) => {
    setSelectedSession(session);
    setViewDialogOpen(true);
  };

  // Edit quiz session
  const editQuizSession = (session) => {
    setSelectedSession(session);
    setEditingSession(session);
    
    // Populate form with existing data
    const config = typeof session.quiz_config === 'string' 
      ? JSON.parse(session.quiz_config) 
      : session.quiz_config;
    
    const rapidConfig = typeof session.rapid_config === 'string' 
      ? JSON.parse(session.rapid_config) 
      : session.rapid_config;
    
    const quizData = typeof session.quiz_data === 'string' 
      ? JSON.parse(session.quiz_data) 
      : session.quiz_data;

    setFormData({
      quiz_name: session.quiz_name,
      quiz_config: {
        difficulty: config.difficulty || 'medium',
        questions_per_song: config.questions_per_song || 3
      },
      quiz_data: {
        questions: quizData.questions || [],
        songs: quizData.songs || []
      },
      rapid_config: {
        questions_per_song: rapidConfig.questions_per_song || 2,
        total_questions: rapidConfig.total_questions || 20,
        scoring_type: rapidConfig.scoring_type || 'speed_bonus',
        allow_skip: rapidConfig.allow_skip || false,
        show_correct_answer: rapidConfig.show_correct_answer || true,
        leaderboard_update_frequency: rapidConfig.leaderboard_update_frequency || 'real_time',
        selected_songs: rapidConfig.selected_songs || []
      },
      scheduled_start_time: session.scheduled_start_time || '',
      scheduled_end_time: session.scheduled_end_time || ''
    });

    // Set selected songs for editing
    if (quizData.songs && quizData.songs.length > 0) {
      setSelectedSongs(quizData.songs);
    }

    setEditDialogOpen(true);
  };

  // Push question for a specific song
  const pushSongQuestion = async (sessionId, songId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/rapid/sessions/${sessionId}/push-song/${songId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to push song question');
      }

      const result = await response.json();
      
      enqueueSnackbar(
        `Questions for "${result.song.name}" pushed successfully! (${result.total_questions} questions)`, 
        { variant: 'success' }
      );
      
      // console.log('Pushed questions:', result.questions);
      fetchSessions(); // Refresh to update status
    } catch (err) {
      enqueueSnackbar('Failed to push song question', { variant: 'error' });
    }
  };

  // View participants
  const viewParticipants = async (session) => {
    try {
      setSelectedSession(session);
      setLoadingParticipants(true);
      setParticipantsDialogOpen(true);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions/${session.session_id}/participants`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch participants');
      }
      
      const data = await response.json();
      setParticipants(data.participants || []);
    } catch (err) {
      enqueueSnackbar('Failed to fetch participants', { variant: 'error' });
      setParticipants([]);
    } finally {
      setLoadingParticipants(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      quiz_name: '',
      quiz_config: {
        difficulty_id: difficulties.length > 0 ? difficulties[0].difficulty_id : null,
        questions_per_song: 3
      },
      quiz_data: {
        questions: [],
        songs: []
      },
      rapid_config: {
        questions_per_song: 2,
        total_questions: 20,
        scoring_type: 'speed_bonus',
        allow_skip: false,
        show_correct_answer: true,
        leaderboard_update_frequency: 'real_time',
        selected_songs: []
      },
      scheduled_start_time: '',
      scheduled_end_time: ''
    });
    setSelectedSongs([]);
    setSongSearchTerm('');
    setEditingSession(null);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleString();
  };

  // Get rapid status color
  const getRapidStatusColor = (status) => {
    switch (status) {
      case 'waiting': return '#FFC107';
      case 'active': return '#4CAF50';
      case 'ended': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  // Load sessions on mount
  useEffect(() => {
    fetchSessions();
    fetchDifficulties();
  }, [fetchSessions, fetchDifficulties]);

  // Fetch songs when difficulty changes or dialog opens
  useEffect(() => {
    if (createDialogOpen || editDialogOpen) {
      fetchSongsWithQuestions(formData.quiz_config.difficulty_id);
    }
  }, [createDialogOpen, editDialogOpen, formData.quiz_config.difficulty_id, fetchSongsWithQuestions]);

  return (
    <ManagementContainer>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <RapidIcon sx={{ fontSize: 32, color: '#FF9800', mr: 2 }} />
          <Typography variant="h4" component="h1" sx={{ color: '#fff', fontWeight: 'bold' }}>
            Rapid Quiz Management
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: '#B0B0B0', mb: 3 }}>
          Manage rapid quiz sessions with admin-controlled question progression
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <ActionButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Rapid Quiz
          </ActionButton>
          <ActionButton
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={fetchSessions}
          >
            Refresh
          </ActionButton>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress sx={{ color: '#FF9800' }} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && sessions.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No rapid quiz sessions found. Create your first rapid quiz session to get started.
        </Alert>
      )}

      <Grid container spacing={3}>
        {sessions.map((session) => (
          <Grid item xs={12} md={6} lg={4} key={session.session_id}>
            <StyledCard>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
                    {session.quiz_name}
                  </Typography>
                  <StatusChip 
                    label={session.status} 
                    status={session.status}
                    size="small"
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Chip 
                    label={`Rapid Status: ${session.rapid_status || 'waiting'}`}
                    size="small"
                    sx={{ 
                      backgroundColor: `${getRapidStatusColor(session.rapid_status)}20`,
                      color: getRapidStatusColor(session.rapid_status),
                      border: `1px solid ${getRapidStatusColor(session.rapid_status)}`,
                      mb: 1
                    }}
                  />
                  <Typography variant="body2" sx={{ color: '#B0B0B0', mb: 1 }}>
                    Question: {session.current_question_index || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#B0B0B0', mb: 1 }}>
                    Participants: {session.participant_count || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#B0B0B0' }}>
                    Created: {formatDate(session.created_at)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Tooltip title="View Quiz Details">
                    <IconButton 
                      size="small" 
                      onClick={() => viewQuizDetails(session)}
                      sx={{ color: '#FF9800' }}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Edit Quiz">
                    <IconButton 
                      size="small" 
                      onClick={() => editQuizSession(session)}
                      sx={{ color: '#4CAF50' }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="View Participants">
                    <IconButton 
                      size="small" 
                      onClick={() => viewParticipants(session)}
                      sx={{ color: '#2196F3' }}
                    >
                      <PeopleIcon />
                    </IconButton>
                  </Tooltip>

                  {session.status === 'active' && session.rapid_status === 'active' && (
                    <Tooltip title="Next Question">
                      <IconButton 
                        size="small" 
                        onClick={() => startNextQuestion(session.session_id)}
                        sx={{ color: '#4CAF50' }}
                      >
                        <NextIcon />
                      </IconButton>
                    </Tooltip>
                  )}

                  {session.status === 'active' && (
                    <Tooltip title="End Session">
                      <IconButton 
                        size="small" 
                        onClick={() => endRapidQuizSession(session.session_id)}
                        sx={{ color: '#F44336' }}
                      >
                        <EndIcon />
                      </IconButton>
                    </Tooltip>
                  )}

                  <Tooltip title={session.status === 'active' ? 'Deactivate' : 'Activate'}>
                    <IconButton 
                      size="small" 
                      onClick={() => toggleSessionStatus(session.session_id, session.status)}
                      sx={{ color: session.status === 'active' ? '#F44336' : '#4CAF50' }}
                    >
                      {session.status === 'active' ? <StopIcon /> : <PlayIcon />}
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete">
                    <IconButton 
                      size="small" 
                      onClick={() => deleteSession(session.session_id)}
                      sx={{ color: '#F44336' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
        ))}
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: '#1A1A1A', color: '#fff' }
        }}
      >
        <DialogTitle sx={{ 
          color: '#fff', 
          borderBottom: '1px solid #333',
          backgroundColor: '#1A1A1A',
          fontSize: '1.5rem',
          fontWeight: 600,
          py: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <RapidIcon sx={{ mr: 2, color: '#FF9800' }} />
            {editingSession ? 'Edit Rapid Quiz Session' : 'Create Rapid Quiz Session'}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ 
          pt: 4, 
          pb: 2,
          backgroundColor: '#1A1A1A',
          minHeight: '500px'
        }}>
          <Grid container spacing={3}>
            {/* Quiz Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Quiz Name"
                value={formData.quiz_name}
                onChange={(e) => setFormData({ ...formData, quiz_name: e.target.value })}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    color: '#fff',
                    backgroundColor: '#121212',
                    '& fieldset': {
                      borderColor: '#333',
                    },
                    '&:hover fieldset': {
                      borderColor: '#FF9800',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FF9800',
                    },
                  },
                  '& .MuiInputLabel-root': { 
                    color: '#B0B0B0',
                    '&.Mui-focused': {
                      color: '#FF9800',
                    }
                  }
                }}
              />
            </Grid>

            {/* Difficulty Selection */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ 
                  color: '#B0B0B0',
                  '&.Mui-focused': {
                    color: '#FF9800',
                  }
                }}>
                  Difficulty
                </InputLabel>
                <Select
                  value={formData.quiz_config.difficulty_id || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    quiz_config: { ...formData.quiz_config, difficulty_id: e.target.value }
                  })}
                  sx={{ 
                    color: '#fff',
                    backgroundColor: '#121212',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#333',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#FF9800',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#FF9800',
                    },
                    '& .MuiSvgIcon-root': {
                      color: '#B0B0B0',
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: '#1A1A1A',
                        border: '1px solid #333',
                        '& .MuiMenuItem-root': {
                          color: '#fff',
                          '&:hover': {
                            backgroundColor: '#333',
                          },
                          '&.Mui-selected': {
                            backgroundColor: '#FF9800',
                            '&:hover': {
                              backgroundColor: '#F57C00',
                            }
                          }
                        }
                      }
                    }
                  }}
                >
                  {difficulties.map((difficulty) => (
                    <MenuItem key={difficulty.difficulty_id} value={difficulty.difficulty_id}>
                      {difficulty.level_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Questions Per Song Ratio Buttons */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ color: '#fff', mb: 2 }}>
                Questions Per Song
              </Typography>
              <Box display="flex" gap={2}>
                {[5, 10, 15].map((count) => (
                  <Button
                    key={count}
                    variant={formData.rapid_config.questions_per_song === count ? "contained" : "outlined"}
                    onClick={() => setFormData({
                      ...formData, 
                      rapid_config: {...formData.rapid_config, questions_per_song: count}
                    })}
                    sx={{
                      backgroundColor: formData.rapid_config.questions_per_song === count ? '#FF9800' : 'transparent',
                      color: formData.rapid_config.questions_per_song === count ? '#fff' : '#FF9800',
                      borderColor: '#FF9800',
                      '&:hover': {
                        backgroundColor: formData.rapid_config.questions_per_song === count ? '#F57C00' : 'rgba(255, 152, 0, 0.1)',
                        borderColor: '#FF9800',
                      },
                      minWidth: '60px',
                    }}
                  >
                    {count}
                  </Button>
                ))}
              </Box>
              
              {/* Display calculated total questions */}
              <Box mt={2}>
                {(() => {
                  const questionsPerSong = formData.rapid_config.questions_per_song || 2;
                  const numberOfSongs = selectedSongs.length;
                  const totalQuestions = questionsPerSong * numberOfSongs;
                  const isValid = questionsPerSong >= 1 && questionsPerSong <= 10;
                  
                  return (
                    <>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: isValid ? '#FF9800' : '#f44336', 
                          fontWeight: 600 
                        }}
                      >
                        Total questions: {totalQuestions} ({numberOfSongs} songs × {questionsPerSong} questions each)
                      </Typography>
                    </>
                  );
                })()}
              </Box>
            </Grid>

            {/* Show Correct Answer Switch */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.rapid_config.show_correct_answer}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      rapid_config: { ...formData.rapid_config, show_correct_answer: e.target.checked }
                    })}
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FF9800' } }}
                  />
                }
                label="Show Correct Answer After Each Question"
                sx={{ color: '#fff' }}
              />
            </Grid>

            {/* Song Selection Section */}
            <Grid item xs={12}>
              <Divider sx={{ borderColor: '#333', my: 2 }} />
              <Typography variant="h6" sx={{ color: '#fff', mb: 2, display: 'flex', alignItems: 'center' }}>
                <MusicIcon sx={{ mr: 1, color: '#FF9800' }} />
                Select Songs ({selectedSongs.length} selected)
              </Typography>
              
              {/* Song Search */}
              <TextField
                fullWidth
                placeholder="Search songs by name or artist..."
                value={songSearchTerm}
                onChange={(e) => handleSongSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#B0B0B0' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': { 
                    color: '#fff',
                    backgroundColor: '#121212',
                    '& fieldset': {
                      borderColor: '#333',
                    },
                    '&:hover fieldset': {
                      borderColor: '#FF9800',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FF9800',
                    },
                  },
                  '& .MuiInputLabel-root': { 
                    color: '#B0B0B0',
                    '&.Mui-focused': {
                      color: '#FF9800',
                    }
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#666',
                    opacity: 1,
                  }
                }}
              />

              {/* Songs List */}
              <Box sx={{ 
                maxHeight: '300px', 
                overflow: 'auto', 
                border: '1px solid #333', 
                borderRadius: '8px',
                backgroundColor: '#121212'
              }}>
                {loadingSongs ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress sx={{ color: '#FF9800' }} />
                    <Typography sx={{ color: '#fff', ml: 2 }}>Loading songs...</Typography>
                  </Box>
                ) : getFilteredSongs().length === 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <Typography sx={{ color: '#666' }}>
                      {songSearchTerm ? 'No songs found matching your search' : 'No songs available'}
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ backgroundColor: '#121212', p: 0 }}>
                    {getFilteredSongs().map((song, index) => {
                      const isSelected = selectedSongs.some(s => s.songs_master_id === song.songs_master_id);
                      return (
                        <ListItem 
                          key={song.songs_master_id} 
                          sx={{ 
                            borderBottom: '1px solid #333',
                            backgroundColor: isSelected ? 'rgba(255, 152, 0, 0.1)' : 'transparent',
                            '&:hover': {
                              backgroundColor: isSelected ? 'rgba(255, 152, 0, 0.2)' : '#1A1A1A'
                            }
                          }}
                        >
                          <Checkbox
                            checked={isSelected}
                            onChange={(e) => handleSongSelection(song, e.target.checked)}
                            sx={{ 
                              color: '#FF9800',
                              '&.Mui-checked': { 
                                color: '#FF9800',
                                backgroundColor: 'rgba(255, 152, 0, 0.1)'
                              }
                            }}
                          />
                          <ListItemText
                            primary={
                              <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                                {song.name}
                              </Typography>
                            }
                            secondary={
                              <Typography sx={{ color: '#B0B0B0' }}>
                                {song.artist} • {song.question_count} questions
                              </Typography>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Chip
                              label={`${song.question_count} questions`}
                              size="small"
                              sx={{
                                backgroundColor: '#FF9800',
                                color: '#fff',
                                fontSize: '0.75rem',
                                fontWeight: 600
                              }}
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Box>

              {/* Selected Songs Summary */}
              <Box sx={{ mt: 2 }}>
                {selectedSongs.length > 0 ? (
                  <>
                    <Typography variant="body2" sx={{ color: '#B0B0B0', mb: 1 }}>
                      Selected Songs ({selectedSongs.length}):
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedSongs.map((song) => (
                        <Chip
                          key={song.songs_master_id}
                          label={`${song.name} - ${song.artist}`}
                          size="small"
                          onDelete={() => handleSongSelection(song, false)}
                          sx={{
                            backgroundColor: '#FF9800',
                            color: '#fff',
                            '& .MuiChip-deleteIcon': { color: '#fff' }
                          }}
                        />
                      ))}
                    </Box>
                  </>
                ) : (
                  <Box sx={{ 
                    p: 2, 
                    border: '1px dashed #333', 
                    borderRadius: '8px',
                    backgroundColor: '#0F0F0F',
                    textAlign: 'center'
                  }}>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      No songs selected. Please select at least one song to create the rapid quiz.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid #333', 
          p: 3, 
          backgroundColor: '#1A1A1A',
          justifyContent: 'space-between'
        }}>
          <Button 
            onClick={() => setCreateDialogOpen(false)}
            sx={{ 
              color: '#B0B0B0',
              border: '1px solid #333',
              px: 3,
              py: 1,
              borderRadius: '8px',
              '&:hover': {
                borderColor: '#555',
                backgroundColor: '#222'
              }
            }}
          >
            Cancel
          </Button>
          <ActionButton 
            onClick={createSession}
            disabled={selectedSongs.length === 0}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 600,
              '&:disabled': {
                backgroundColor: 'rgba(255, 152, 0, 0.3)',
                color: 'rgba(255, 255, 255, 0.5)'
              }
            }}
          >
            {editingSession ? 'Update' : 'Create'} Rapid Quiz
          </ActionButton>
        </DialogActions>
      </Dialog>

      {/* View Quiz Details Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: '#1A1A1A', color: '#fff' }
        }}
      >
        <DialogTitle sx={{ 
          color: '#fff', 
          borderBottom: '1px solid #333',
          backgroundColor: '#1A1A1A',
          fontSize: '1.5rem',
          fontWeight: 600,
          py: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ViewIcon sx={{ mr: 2, color: '#FF9800' }} />
            Quiz Details - {selectedSession?.quiz_name}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, pb: 2, backgroundColor: '#1A1A1A' }}>
          {selectedSession && (() => {
            const config = typeof selectedSession.quiz_config === 'string' 
              ? JSON.parse(selectedSession.quiz_config) 
              : selectedSession.quiz_config;
            
            const rapidConfig = typeof selectedSession.rapid_config === 'string' 
              ? JSON.parse(selectedSession.rapid_config) 
              : selectedSession.rapid_config;
            
            const quizData = typeof selectedSession.quiz_data === 'string' 
              ? JSON.parse(selectedSession.quiz_data) 
              : selectedSession.quiz_data;

            return (
              <Grid container spacing={3}>
                {/* Basic Info */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ color: '#FF9800', mb: 2 }}>
                    Basic Information
                  </Typography>
                  <Box sx={{ p: 2, backgroundColor: '#121212', borderRadius: '8px' }}>
                    <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
                      <strong>Quiz Name:</strong> {selectedSession.quiz_name}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
                      <strong>Status:</strong> {selectedSession.status}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
                      <strong>Rapid Status:</strong> {selectedSession.rapid_status || 'waiting'}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
                      <strong>Current Question:</strong> {selectedSession.current_question_index || 0}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
                      <strong>Participants:</strong> {selectedSession.participant_count || 0}
                    </Typography>
                    {rapidConfig.current_song_id && (
                      <Typography variant="body1" sx={{ color: '#fff' }}>
                        <strong>Active Song:</strong> {quizData.current_song?.name || 'Unknown'}
                      </Typography>
                    )}
                  </Box>
                </Grid>

                {/* Configuration */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="h6" sx={{ color: '#FF9800', mb: 2 }}>
                    Configuration
                  </Typography>
                  <Box sx={{ p: 2, backgroundColor: '#121212', borderRadius: '8px' }}>
                    <Typography variant="body2" sx={{ color: '#fff', mb: 1 }}>
                      <strong>Difficulty:</strong> {config.difficulty || 'Medium'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#fff', mb: 1 }}>
                      <strong>Questions per Song:</strong> {config.questions_per_song || 3}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#fff', mb: 1 }}>
                      <strong>Number of Songs:</strong> {config.number_of_songs || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#fff' }}>
                      <strong>Show Correct Answer:</strong> {rapidConfig.show_correct_answer ? 'Yes' : 'No'}
                    </Typography>
                  </Box>
                </Grid>

                {/* Songs List */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ color: '#FF9800', mb: 2 }}>
                    Selected Songs ({quizData.songs?.length || 0})
                  </Typography>
                  <Box sx={{ maxHeight: '300px', overflow: 'auto' }}>
                    {quizData.songs && quizData.songs.length > 0 ? (
                      <List sx={{ backgroundColor: '#121212', borderRadius: '8px' }}>
                        {quizData.songs.map((song, index) => (
                          <ListItem key={song.songs_master_id} sx={{ borderBottom: '1px solid #333' }}>
                            <ListItemText
                              primary={
                                <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                                  {index + 1}. {song.name}
                                </Typography>
                              }
                              secondary={
                                <Typography sx={{ color: '#B0B0B0' }}>
                                  {song.artist} • {song.question_count} questions
                                </Typography>
                              }
                            />
                            <ListItemSecondaryAction>
                              {selectedSession.status === 'active' && (
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => pushSongQuestion(selectedSession.session_id, song.songs_master_id)}
                                  sx={{
                                    backgroundColor: '#FF9800',
                                    color: '#fff',
                                    '&:hover': { backgroundColor: '#F57C00' }
                                  }}
                                >
                                  Push Question
                                </Button>
                              )}
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Box sx={{ p: 3, textAlign: 'center', backgroundColor: '#121212', borderRadius: '8px' }}>
                        <Typography sx={{ color: '#666' }}>No songs selected</Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid #333', 
          p: 3, 
          backgroundColor: '#1A1A1A',
          justifyContent: 'center'
        }}>
          <Button 
            onClick={() => setViewDialogOpen(false)}
            sx={{ 
              color: '#B0B0B0',
              border: '1px solid #333',
              px: 3,
              py: 1,
              borderRadius: '8px',
              '&:hover': {
                borderColor: '#555',
                backgroundColor: '#222'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Quiz Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: '#1A1A1A', color: '#fff' }
        }}
      >
        <DialogTitle sx={{ 
          color: '#fff', 
          borderBottom: '1px solid #333',
          backgroundColor: '#1A1A1A',
          fontSize: '1.5rem',
          fontWeight: 600,
          py: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EditIcon sx={{ mr: 2, color: '#4CAF50' }} />
            Edit Rapid Quiz Session
          </Box>
        </DialogTitle>
        <DialogContent sx={{ 
          pt: 4, 
          pb: 2,
          backgroundColor: '#1A1A1A',
          minHeight: '500px'
        }}>
          <Grid container spacing={3}>
            {/* Quiz Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Quiz Name"
                value={formData.quiz_name}
                onChange={(e) => setFormData({ ...formData, quiz_name: e.target.value })}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    color: '#fff',
                    backgroundColor: '#121212',
                    '& fieldset': {
                      borderColor: '#333',
                    },
                    '&:hover fieldset': {
                      borderColor: '#4CAF50',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#4CAF50',
                    },
                  },
                  '& .MuiInputLabel-root': { 
                    color: '#B0B0B0',
                    '&.Mui-focused': {
                      color: '#4CAF50',
                    }
                  }
                }}
              />
            </Grid>

            {/* Difficulty Selection */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ 
                  color: '#B0B0B0',
                  '&.Mui-focused': {
                    color: '#4CAF50',
                  }
                }}>
                  Difficulty
                </InputLabel>
                <Select
                  value={formData.quiz_config.difficulty_id || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    quiz_config: { ...formData.quiz_config, difficulty_id: e.target.value }
                  })}
                  sx={{ 
                    color: '#fff',
                    backgroundColor: '#121212',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#333',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4CAF50',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4CAF50',
                    },
                    '& .MuiSvgIcon-root': {
                      color: '#B0B0B0',
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: '#1A1A1A',
                        border: '1px solid #333',
                        '& .MuiMenuItem-root': {
                          color: '#fff',
                          '&:hover': {
                            backgroundColor: '#333',
                          },
                          '&.Mui-selected': {
                            backgroundColor: '#4CAF50',
                            '&:hover': {
                              backgroundColor: '#45a049',
                            }
                          }
                        }
                      }
                    }
                  }}
                >
                  {difficulties.map((difficulty) => (
                    <MenuItem key={difficulty.difficulty_id} value={difficulty.difficulty_id}>
                      {difficulty.level_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Questions Per Song Ratio Buttons */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ color: '#fff', mb: 2 }}>
                Questions Per Song
              </Typography>
              <Box display="flex" gap={2}>
                {[1, 3, 5, 10, 15].map((count) => (
                  <Button
                    key={count}
                    variant={formData.rapid_config.questions_per_song === count ? "contained" : "outlined"}
                    onClick={() => setFormData({
                      ...formData, 
                      rapid_config: {...formData.rapid_config, questions_per_song: count}
                    })}
                    sx={{
                      backgroundColor: formData.rapid_config.questions_per_song === count ? '#4CAF50' : 'transparent',
                      color: formData.rapid_config.questions_per_song === count ? '#fff' : '#4CAF50',
                      borderColor: '#4CAF50',
                      '&:hover': {
                        backgroundColor: formData.rapid_config.questions_per_song === count ? '#45a049' : 'rgba(76, 175, 80, 0.1)',
                        borderColor: '#4CAF50',
                      },
                      minWidth: '60px',
                    }}
                  >
                    {count}
                  </Button>
                ))}
              </Box>
              
              {/* Display calculated total questions */}
              <Box mt={2}>
                {(() => {
                  const questionsPerSong = formData.rapid_config.questions_per_song || 2;
                  const numberOfSongs = selectedSongs.length;
                  const totalQuestions = questionsPerSong * numberOfSongs;
                  const isValid = questionsPerSong >= 1 && questionsPerSong <= 10;
                  
                  return (
                    <>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: isValid ? '#4CAF50' : '#f44336', 
                          fontWeight: 600 
                        }}
                      >
                        Total questions: {totalQuestions} ({numberOfSongs} songs × {questionsPerSong} questions each)
                      </Typography>
                    </>
                  );
                })()}
              </Box>
            </Grid>

            {/* Show Correct Answer Switch */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.rapid_config.show_correct_answer}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      rapid_config: { ...formData.rapid_config, show_correct_answer: e.target.checked }
                    })}
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4CAF50' } }}
                  />
                }
                label="Show Correct Answer After Each Question"
                sx={{ color: '#fff' }}
              />
            </Grid>

            {/* Song Selection Section */}
            <Grid item xs={12}>
              <Divider sx={{ borderColor: '#333', my: 2 }} />
              <Typography variant="h6" sx={{ color: '#fff', mb: 2, display: 'flex', alignItems: 'center' }}>
                <MusicIcon sx={{ mr: 1, color: '#4CAF50' }} />
                Select Songs ({selectedSongs.length} selected)
              </Typography>
              
              {/* Song Search */}
              <TextField
                fullWidth
                placeholder="Search songs by name or artist..."
                value={songSearchTerm}
                onChange={(e) => handleSongSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#B0B0B0' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': { 
                    color: '#fff',
                    backgroundColor: '#121212',
                    '& fieldset': {
                      borderColor: '#333',
                    },
                    '&:hover fieldset': {
                      borderColor: '#4CAF50',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#4CAF50',
                    },
                  },
                  '& .MuiInputLabel-root': { 
                    color: '#B0B0B0',
                    '&.Mui-focused': {
                      color: '#4CAF50',
                    }
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#666',
                    opacity: 1,
                  }
                }}
              />

              {/* Songs List */}
              <Box sx={{ 
                maxHeight: '300px', 
                overflow: 'auto', 
                border: '1px solid #333', 
                borderRadius: '8px',
                backgroundColor: '#121212'
              }}>
                {loadingSongs ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress sx={{ color: '#4CAF50' }} />
                    <Typography sx={{ color: '#fff', ml: 2 }}>Loading songs...</Typography>
                  </Box>
                ) : getFilteredSongs().length === 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <Typography sx={{ color: '#666' }}>
                      {songSearchTerm ? 'No songs found matching your search' : 'No songs available'}
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ backgroundColor: '#121212', p: 0 }}>
                    {getFilteredSongs().map((song, index) => {
                      const isSelected = selectedSongs.some(s => s.songs_master_id === song.songs_master_id);
                      return (
                        <ListItem 
                          key={song.songs_master_id} 
                          sx={{ 
                            borderBottom: '1px solid #333',
                            backgroundColor: isSelected ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                            '&:hover': {
                              backgroundColor: isSelected ? 'rgba(76, 175, 80, 0.2)' : '#1A1A1A'
                            }
                          }}
                        >
                          <Checkbox
                            checked={isSelected}
                            onChange={(e) => handleSongSelection(song, e.target.checked)}
                            sx={{ 
                              color: '#4CAF50',
                              '&.Mui-checked': { 
                                color: '#4CAF50',
                                backgroundColor: 'rgba(76, 175, 80, 0.1)'
                              }
                            }}
                          />
                          <ListItemText
                            primary={
                              <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                                {song.name}
                              </Typography>
                            }
                            secondary={
                              <Typography sx={{ color: '#B0B0B0' }}>
                                {song.artist} • {song.question_count} questions
                              </Typography>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Chip
                              label={`${song.question_count} questions`}
                              size="small"
                              sx={{
                                backgroundColor: '#4CAF50',
                                color: '#fff',
                                fontSize: '0.75rem',
                                fontWeight: 600
                              }}
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Box>

              {/* Selected Songs Summary */}
              <Box sx={{ mt: 2 }}>
                {selectedSongs.length > 0 ? (
                  <>
                    <Typography variant="body2" sx={{ color: '#B0B0B0', mb: 1 }}>
                      Selected Songs ({selectedSongs.length}):
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedSongs.map((song) => (
                        <Chip
                          key={song.songs_master_id}
                          label={`${song.name} - ${song.artist}`}
                          size="small"
                          onDelete={() => handleSongSelection(song, false)}
                          sx={{
                            backgroundColor: '#4CAF50',
                            color: '#fff',
                            '& .MuiChip-deleteIcon': { color: '#fff' }
                          }}
                        />
                      ))}
                    </Box>
                  </>
                ) : (
                  <Box sx={{ 
                    p: 2, 
                    border: '1px dashed #333', 
                    borderRadius: '8px',
                    backgroundColor: '#0F0F0F',
                    textAlign: 'center'
                  }}>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      No songs selected. Please select at least one song to update the rapid quiz.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid #333', 
          p: 3, 
          backgroundColor: '#1A1A1A',
          justifyContent: 'space-between'
        }}>
          <Button 
            onClick={() => setEditDialogOpen(false)}
            sx={{ 
              color: '#B0B0B0',
              border: '1px solid #333',
              px: 3,
              py: 1,
              borderRadius: '8px',
              '&:hover': {
                borderColor: '#555',
                backgroundColor: '#222'
              }
            }}
          >
            Cancel
          </Button>
          <ActionButton 
            onClick={createSession}
            disabled={selectedSongs.length === 0}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 600,
              backgroundColor: '#4CAF50',
              '&:hover': {
                backgroundColor: '#45a049'
              },
              '&:disabled': {
                backgroundColor: 'rgba(76, 175, 80, 0.3)',
                color: 'rgba(255, 255, 255, 0.5)'
              }
            }}
          >
            Update Rapid Quiz
          </ActionButton>
        </DialogActions>
      </Dialog>

      {/* Participants Dialog */}
      <Dialog 
        open={participantsDialogOpen} 
        onClose={() => setParticipantsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: '#1A1A1A', color: '#fff' }
        }}
      >
        <DialogTitle sx={{ color: '#fff', borderBottom: '1px solid #333' }}>
          Participants - {selectedSession?.quiz_name}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {loadingParticipants ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#FF9800' }} />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ backgroundColor: '#2A2A2A' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Score</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Position</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Joined</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {participants.map((participant, index) => (
                    <TableRow key={participant.participant_id || index}>
                      <TableCell sx={{ color: '#fff' }}>
                        {participant.fname} {participant.lname || ''}
                        {participant.guest_name && ` (${participant.guest_name})`}
                      </TableCell>
                      <TableCell sx={{ color: '#fff' }}>
                        {participant.current_score || 0}
                      </TableCell>
                      <TableCell sx={{ color: '#fff' }}>
                        {participant.current_position || '-'}
                      </TableCell>
                      <TableCell sx={{ color: '#fff' }}>
                        {formatDate(participant.joined_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #333', p: 2 }}>
          <Button 
            onClick={() => setParticipantsDialogOpen(false)}
            sx={{ color: '#B0B0B0' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </ManagementContainer>
  );
};

export default RapidQuizManagement;
